import { useState } from "preact/hooks";
import type { Ctx } from "../app";
import { api, must, sb, smsConfig } from "../lib/supabase";
import { OutboxLog } from "./messages";
import { Badge, Card, ErrorBox, Loading, Modal, fmtDate, formData, toast, useAction, useLoad } from "../lib/ui";
import { parseCsv } from "../../../src/shared/csv";

const TABS: Array<[string, string]> = [["users", "Users & roles"], ["entities", "Entities & group contracts"], ["import", "Import"], ["messaging", "Messaging & alerts"], ["integrations", "Google Sheets & integrations"], ["audit", "Audit log"]];

export function AdminPage({ ctx, tab = "users" }: { ctx: Ctx; tab?: string }) {
  return (
    <>
      <nav class="tabs">{TABS.map(([id, label]) => <a href={`#/admin/${id}`} class={tab === id ? "active" : ""}>{label}</a>)}</nav>
      {tab === "users" && <Users ctx={ctx} />}
      {tab === "entities" && <Entities ctx={ctx} />}
      {tab === "import" && <Import ctx={ctx} />}
      {tab === "audit" && <Audit ctx={ctx} />}
      {tab === "integrations" && <Integrations ctx={ctx} />}
      {tab === "messaging" && <Messaging ctx={ctx} />}
    </>
  );
}

// ------------------------------------------------------------------ users
const ROLE_HELP: Record<string, string> = {
  owner: "Everything, including managing owners",
  admin: "Everything except managing owners",
  staff: "Add/edit BCBAs, applications, documents; no deletes",
  vendor: "Outsourced credentialing company: same as staff",
  viewer: "Read-only",
};

function Users({ ctx }: { ctx: Ctx }) {
  const [inviting, setInviting] = useState(false);
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () =>
    must(await sb().from("org_members").select("*").eq("org_id", ctx.orgId).order("active", { ascending: false }).order("email")) as any[], [ctx.orgId]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const roles = ctx.role === "owner" ? Object.keys(ROLE_HELP) : Object.keys(ROLE_HELP).filter((r) => r !== "owner");
  const update = (user_id: string, patch: Record<string, unknown>, msg: string) =>
    run(async () => { must(await sb().from("org_members").update(patch).eq("org_id", ctx.orgId).eq("user_id", user_id).select("user_id").single()); reload(); }, msg);

  return (
    <Card title="Users & roles" actions={<button onClick={() => setInviting(true)}>Invite user</button>}>
      <ul class="muted small">{Object.entries(ROLE_HELP).map(([r, h]) => <li><strong>{r}</strong>: {h}</li>)}</ul>
      <table>
        <thead><tr><th>User</th><th>Role</th><th>Status</th><th>Added</th><th></th></tr></thead>
        <tbody>{data.map((m) => {
          const self = m.user_id === ctx.userId;
          const locked = self || (m.role === "owner" && ctx.role !== "owner");
          return (
            <tr class={m.active ? "" : "done"}>
              <td>{m.display_name ?? "—"}<div class="muted small">{m.email}</div></td>
              <td>{locked ? <Badge value={m.role} /> : (
                <select value={m.role} disabled={busy} onChange={(e) => update(m.user_id, { role: e.currentTarget.value }, "Role updated")}>
                  {roles.map((r) => <option value={r}>{r}</option>)}</select>)}</td>
              <td>{m.active ? <Badge value="active" /> : <span class="badge">deactivated</span>}</td>
              <td>{fmtDate(m.created_at)}</td>
              <td class="btns">{!locked && (m.active
                ? <button class="small ghost danger" disabled={busy} onClick={() => confirm(`Remove access for ${m.email}?`) && update(m.user_id, { active: false }, "Access removed")}>Deactivate</button>
                : <button class="small ghost" disabled={busy} onClick={() => update(m.user_id, { active: true }, "Access restored")}>Reactivate</button>)}
                {m.active && !self && <button class="small ghost" disabled={busy} onClick={() => run(() => api("/api/app/members", { org_id: ctx.orgId, email: m.email, role: m.role, display_name: m.display_name }), "Sign-in link re-sent")}>Re-send link</button>}
              </td>
            </tr>);
        })}</tbody>
      </table>
      {inviting && (
        <Modal title="Invite user" onClose={() => setInviting(false)}>
          <form class="grid2" onSubmit={(e) => {
            const f = formData(e);
            run(async () => { await api("/api/app/members", { org_id: ctx.orgId, email: f.email, role: f.role, display_name: f.display_name }); setInviting(false); reload(); }, "Invitation sent");
          }}>
            <label>Name<input name="display_name" /></label>
            <label>Email<input name="email" type="email" required /></label>
            <label>Role<select name="role">{roles.map((r) => <option value={r} selected={r === "staff"}>{r}</option>)}</select></label>
            <p class="span2 muted small">They'll get an email with a one-time sign-in link. BCBAs are not users — invite them from their BCBA page with “Send intake link”.</p>
            <div class="span2 row-end"><button disabled={busy}>Send invitation</button></div>
          </form>
        </Modal>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ entities & group contracts
function Entities({ ctx }: { ctx: Ctx }) {
  const [editing, setEditing] = useState<any | null>(null);
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () => {
    const [ents, contracts, payers] = await Promise.all([
      sb().from("legal_entities").select("*").eq("org_id", ctx.orgId).order("legal_name"),
      sb().from("entity_payer_contracts").select("*").eq("org_id", ctx.orgId),
      sb().from("payers").select("id, name, states").order("priority_tier").order("name"),
    ]);
    return { ents: must(ents) as any[], contracts: must(contracts) as any[], payers: must(payers) as any[] };
  }, [ctx.orgId]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;

  const setContract = (entity_id: string, payer_id: string, status: string) => run(async () => {
    must(await sb().from("entity_payer_contracts").upsert({ org_id: ctx.orgId, entity_id, payer_id, status }, { onConflict: "entity_id,payer_id" }));
    reload();
  }, "Saved");

  return (
    <>
      <Card title="Legal entities" actions={<button onClick={() => setEditing({})}>Add entity</button>}>
        <p class="muted small">Only the last 4 digits of each tax ID are stored. Supabase also encrypts all data at rest (AES-256).</p>
        <table>
          <thead><tr><th>Legal name</th><th>DBA</th><th>TIN</th><th>Group NPI</th><th>States</th><th></th></tr></thead>
          <tbody>{data.ents.map((e) => (
            <tr><td>{e.legal_name}</td><td>{e.dba ?? "—"}</td><td>{e.tin_last4 ? `•••••${e.tin_last4}` : "—"}</td>
              <td>{e.group_npi ?? "—"}</td><td>{e.states.join(", ")}</td>
              <td><button class="small ghost" onClick={() => setEditing(e)}>Edit</button></td></tr>))}</tbody>
        </table>
      </Card>
      <Card title="Group contracts (is each entity itself in network?)">
        {data.ents.length === 0 ? <p class="muted">Add an entity first.</p> : data.ents.map((e) => (
          <details class="checklist"><summary><strong>{e.legal_name}</strong></summary>
            <table><tbody>{data.payers.filter((p) => p.states.some((s: string) => e.states.includes(s))).map((p) => {
              const c = data.contracts.find((x) => x.entity_id === e.id && x.payer_id === p.id);
              return (<tr><td>{p.name}</td><td>
                <select value={c?.status ?? "unknown"} disabled={busy} onChange={(ev) => setContract(e.id, p.id, ev.currentTarget.value)}>
                  {["unknown", "not_contracted", "in_progress", "contracted", "terminated"].map((s) => <option value={s}>{s.replace("_", " ")}</option>)}
                </select></td></tr>);
            })}</tbody></table>
          </details>))}
      </Card>
      {editing && (
        <Modal title={editing.id ? "Edit entity" : "Add entity"} onClose={() => setEditing(null)}>
          <form class="grid2" onSubmit={(ev) => {
            const f = formData(ev);
            const form = ev.currentTarget as HTMLFormElement;
            const states = ["NY", "NJ"].filter((s) => (form.elements.namedItem(`st_${s}`) as HTMLInputElement).checked);
            const row = { legal_name: f.legal_name, dba: f.dba, tin_last4: f.tin_last4, group_npi: f.group_npi, states };
            run(async () => {
              if (editing.id) must(await sb().from("legal_entities").update(row).eq("id", editing.id));
              else must(await sb().from("legal_entities").insert({ ...row, org_id: ctx.orgId }));
              setEditing(null); reload();
            }, "Saved");
          }}>
            <label>Legal name<input name="legal_name" required value={editing.legal_name ?? ""} /></label>
            <label>DBA<input name="dba" value={editing.dba ?? ""} /></label>
            <label>TIN — last 4 digits only<input name="tin_last4" pattern="\d{4}" maxLength={4} inputMode="numeric" value={editing.tin_last4 ?? ""} /></label>
            <label>Group NPI (Type 2)<input name="group_npi" pattern="\d{10}" inputMode="numeric" value={editing.group_npi ?? ""} /></label>
            <fieldset><legend>States</legend>{["NY", "NJ"].map((s) => <label class="inline"><input type="checkbox" name={`st_${s}`} checked={editing.states?.includes(s)} /> {s}</label>)}</fieldset>
            <div class="span2 row-end"><button disabled={busy}>Save</button></div>
          </form>
        </Modal>
      )}
    </>
  );
}

// ------------------------------------------------------------------ CSV import
const PROVIDER_COLS = "first_name,last_name,email,phone,npi,caqh_id,practice_states,ny_lba_number,ny_lba_expires,nj_lba_number,nj_lba_expires,bacb_number,bacb_expires,malpractice_expires,caqh_attested_on";
const APP_COLS = "provider_email,payer_slug,entity_name,status,submitted_on,payer_reference_no,effective_on,notes";

function Import({ ctx }: { ctx: Ctx }) {
  const [log, setLog] = useState<string[]>([]);
  const { busy, run } = useAction();
  const add = (l: string) => setLog((x) => [...x, l]);

  const importProviders = (rows: Array<Record<string, string>>) => run(async () => {
    setLog([]);
    let ok = 0;
    for (const [i, r] of rows.entries()) {
      try {
        const states = (r.practice_states ?? "").toUpperCase().split(/[^A-Z]+/).filter((s) => s === "NY" || s === "NJ");
        const p = must(await sb().from("providers").upsert({
          org_id: ctx.orgId, first_name: r.first_name, last_name: r.last_name, email: r.email?.toLowerCase(), phone: r.phone || null,
          npi: r.npi || null, caqh_id: r.caqh_id || null, practice_states: states,
        }, { onConflict: "org_id,email", ignoreDuplicates: false }).select("id").single()) as { id: string };
        const creds = [
          ["ny_lba", "NY", r.ny_lba_number, r.ny_lba_expires], ["nj_lba", "NJ", r.nj_lba_number, r.nj_lba_expires],
          ["bacb_certification", null, r.bacb_number, r.bacb_expires], ["malpractice", null, null, r.malpractice_expires],
        ].filter(([, , num, exp]) => num || exp).map(([kind, state, number, expires_on]) =>
          ({ org_id: ctx.orgId, provider_id: p.id, kind, state, number: number || null, expires_on: expires_on || null, verification_source: "imported" }));
        if (r.caqh_attested_on) creds.push({ org_id: ctx.orgId, provider_id: p.id, kind: "caqh_attestation", state: null, number: null,
          expires_on: addDays(r.caqh_attested_on, 120), verification_source: "imported", ...{ issued_on: r.caqh_attested_on } } as any);
        if (creds.length) must(await sb().from("provider_credentials").insert(creds));
        ok++;
      } catch (e) {
        add(`Row ${i + 2} (${r.email ?? "?"}): ${(e as Error).message}`);
      }
    }
    add(`Imported ${ok} of ${rows.length} BCBAs.`);
  });

  const importApps = (rows: Array<Record<string, string>>) => run(async () => {
    setLog([]);
    const [provs, payers, ents] = await Promise.all([
      sb().from("providers").select("id, email").eq("org_id", ctx.orgId),
      sb().from("payers").select("id, slug"),
      sb().from("legal_entities").select("id, legal_name").eq("org_id", ctx.orgId),
    ]);
    const pmap = new Map((must(provs) as any[]).map((p) => [p.email.toLowerCase(), p.id]));
    const ymap = new Map((must(payers) as any[]).map((p) => [p.slug, p.id]));
    const emap = new Map((must(ents) as any[]).map((e) => [e.legal_name.toLowerCase(), e.id]));
    let ok = 0;
    for (const [i, r] of rows.entries()) {
      const provider_id = pmap.get((r.provider_email ?? "").toLowerCase());
      const payer_id = ymap.get(r.payer_slug ?? "");
      if (!provider_id || !payer_id) { add(`Row ${i + 2}: unknown ${!provider_id ? "provider_email" : "payer_slug"}`); continue; }
      try {
        must(await sb().from("applications").insert({
          org_id: ctx.orgId, provider_id, payer_id, entity_id: emap.get((r.entity_name ?? "").toLowerCase()) ?? null,
          status: r.status || "not_started", submitted_on: r.submitted_on || null, payer_reference_no: r.payer_reference_no || null,
          effective_on: r.effective_on || null, notes: r.notes || null,
        }));
        ok++;
      } catch (e) { add(`Row ${i + 2}: ${(e as Error).message}`); }
    }
    add(`Imported ${ok} of ${rows.length} applications.`);
  });

  const pick = (handler: (rows: Array<Record<string, string>>) => void) => (e: Event) => {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (file) file.text().then((t) => handler(parseCsv(t)));
  };

  return (
    <Card title="Import from the existing spreadsheet / ClickUp export">
      <p>Save the sheet as CSV with these column headers (extra columns are ignored; dates as YYYY-MM-DD). <strong>Remove SSN and date-of-birth columns first</strong> — they will be rejected.</p>
      <h3>1. BCBAs</h3>
      <code class="block">{PROVIDER_COLS}</code>
      <input type="file" accept=".csv" disabled={busy} onChange={pick(importProviders)} />
      <h3>2. Applications</h3>
      <p class="muted small">payer_slug values are listed on the Payers page URLs (e.g. <code>aetna_commercial</code>, <code>nj_horizon_nj_health</code>).</p>
      <code class="block">{APP_COLS}</code>
      <input type="file" accept=".csv" disabled={busy} onChange={pick(importApps)} />
      {busy && <p>Importing…</p>}
      {log.length > 0 && <pre class="log">{log.join("\n")}</pre>}
    </Card>
  );
}
const addDays = (d: string, n: number) => new Date(new Date(`${d}T12:00:00Z`).getTime() + n * 86400000).toISOString().slice(0, 10);

// ------------------------------------------------------------------ audit
function Audit({ ctx }: { ctx: Ctx }) {
  const [table, setTable] = useState("");
  const { data, error } = useLoad(async () => {
    let q = sb().from("audit_log").select("*").eq("org_id", ctx.orgId).order("at", { ascending: false }).limit(200);
    if (table) q = q.eq("table_name", table);
    const rows = must(await q) as any[];
    const members = must(await sb().from("org_members").select("user_id, email").eq("org_id", ctx.orgId)) as any[];
    const who = new Map(members.map((m) => [m.user_id, m.email]));
    return rows.map((r) => ({ ...r, who: r.actor ? who.get(r.actor) ?? "BCBA / other user" : "system" }));
  }, [ctx.orgId, table]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  return (
    <Card title="Audit log (latest 200 changes)" actions={
      <select value={table} onChange={(e) => setTable(e.currentTarget.value)} aria-label="Table">
        <option value="">All tables</option>
        {["providers", "provider_credentials", "provider_documents", "applications", "application_events", "org_members", "legal_entities", "tasks", "email_drafts", "validation_checks"].map((t) => <option value={t}>{t}</option>)}
      </select>}>
      <table>
        <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Table</th><th>Changed fields</th></tr></thead>
        <tbody>{data.map((r) => (
          <tr><td class="small">{new Date(r.at).toLocaleString()}</td><td class="small">{r.who}</td><td><Badge value={r.action.toLowerCase()} /></td>
            <td class="small">{r.table_name}</td><td class="small">{changed(r)}</td></tr>))}</tbody>
      </table>
    </Card>
  );
}
function changed(r: any): string {
  if (r.action !== "UPDATE") return r.action === "INSERT" ? "created" : "deleted";
  const keys = Object.keys(r.new_row ?? {}).filter((k) => k !== "updated_at" && JSON.stringify(r.new_row[k]) !== JSON.stringify(r.old_row?.[k]));
  return keys.map((k) => `${k}: ${short(r.old_row?.[k])} → ${short(r.new_row[k])}`).join("; ") || "—";
}
const short = (v: unknown) => { const s = v === null || v === undefined ? "∅" : String(v); return s.length > 40 ? `${s.slice(0, 40)}…` : s; };

// ------------------------------------------------------------------ integrations (Sheets / Excel)
function Integrations({ ctx }: { ctx: Ctx }) {
  const [fresh, setFresh] = useState<string | null>(null);
  const [keyForFormulas, setKeyForFormulas] = useState("YOUR_KEY");
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () => {
    const [tokens, exportsList] = await Promise.all([
      sb().from("api_tokens").select("*").eq("org_id", ctx.orgId).order("created_at", { ascending: false }),
      fetch("/api/exports").then((r) => r.json()),
    ]);
    return { tokens: must(tokens) as any[], exportsList: exportsList as Array<{ id: string; label: string }> };
  }, [ctx.orgId]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const base = `${location.origin}/api/export`;
  const copy = (t: string) => navigator.clipboard.writeText(t).then(() => toast("Copied"));

  return (
    <>
      <Card title="Read-only keys" actions={<button disabled={busy} onClick={() => {
        const name = prompt("Name this key (e.g. 'Owner's Google Sheet'):");
        if (name !== null) run(async () => {
          const r = await api<{ token: string }>("/api/app/tokens", { org_id: ctx.orgId, name });
          setFresh(r.token); setKeyForFormulas(r.token); reload();
        });
      }}>Create key</button>}>
        <p class="small">A key lets a spreadsheet pull <strong>read-only</strong> data from this tracker. Anyone who has the full link can read that data,
          so treat it like a password: put it only in sheets you control, and revoke it if a sheet is shared or a person leaves.
          There are no SSNs, dates of birth or client data in the system, so none can leave through a key.</p>
        {fresh && <div class="banner" style={{ textAlign: "left" }}><strong>Copy this key now — it won't be shown again:</strong>
          <code class="copy">{fresh}</code><button class="small" onClick={() => copy(fresh)}>Copy key</button></div>}
        <table>
          <thead><tr><th>Name</th><th>Key starts with</th><th>Created</th><th>Last used</th><th></th></tr></thead>
          <tbody>{data.tokens.map((t) => (
            <tr class={t.revoked_at ? "done" : ""}>
              <td>{t.name}</td><td><code>{t.prefix}…</code></td><td>{fmtDate(t.created_at)}</td><td>{t.last_used_at ? fmtDate(t.last_used_at) : "never"}</td>
              <td>{t.revoked_at ? <span class="badge">revoked</span> : <button class="small ghost danger" disabled={busy} onClick={() => confirm(`Revoke "${t.name}"? Sheets using it will stop updating.`) && run(async () => {
                must(await sb().from("api_tokens").update({ revoked_at: new Date().toISOString() }).eq("id", t.id)); reload();
              }, "Key revoked")}>Revoke</button>}</td>
            </tr>))}</tbody>
        </table>
      </Card>

      <Card title="Google Sheets formulas">
        <p class="small">Paste into any cell. Google refreshes IMPORTDATA about once an hour. {keyForFormulas === "YOUR_KEY" && <>Replace <code>YOUR_KEY</code> with a key from above.</>}</p>
        <table><tbody>{data.exportsList.map((x) => {
          const f = `=IMPORTDATA("${base}/${x.id}.csv?key=${keyForFormulas}")`;
          return <tr><td>{x.label}</td><td><code class="copy">{f}</code></td><td><button class="small ghost" onClick={() => copy(f)}>Copy</button></td></tr>;
        })}</tbody></table>
        <h3>Handy formulas on top of the data</h3>
        <ul class="small">
          <li>Filter expiring items (Sheet "Expirations" in A:D): <code>=FILTER(A2:D, D2:D&lt;=30)</code></li>
          <li>Count applications by status (Sheet "Applications", status in E): <code>=QUERY(A1:P, "select E, count(A) group by E", 1)</code></li>
          <li>BCBAs below 100% ready (Sheet "BCBAs", readiness in column I): <code>=FILTER(A2:Q, I2:I&lt;100)</code></li>
        </ul>
      </Card>

      <Card title="Deadline calendar (Google Calendar, Outlook, Apple)">
        <p class="small">Every expiration, payer follow-up date and open manual task as an all-day event. Refreshes every few hours.</p>
        <p><code class="copy">{`${location.origin}/api/calendar.ics?key=${keyForFormulas}`}</code>
          <button class="small ghost" onClick={() => copy(`${location.origin}/api/calendar.ics?key=${keyForFormulas}`)}>Copy</button></p>
        <ul class="small">
          <li><strong>Google Calendar:</strong> Other calendars → + → From URL → paste.</li>
          <li><strong>Outlook:</strong> Add calendar → Subscribe from web → paste.</li>
          <li><strong>iPhone/Mac:</strong> Calendar → File → New Calendar Subscription (or open the link with <code>webcal://</code>).</li>
        </ul>
      </Card>

      <Card title="Excel, Power BI and other tools">
        <ul class="small">
          <li><strong>Excel:</strong> Data → Get Data → From Web → paste a link like <code>{base}/applications.csv?key=…</code>, then Data → Refresh All (or set auto-refresh in Query Properties).</li>
          <li><strong>Power BI / Looker Studio / Airtable:</strong> use the same link, or <code>.json</code> instead of <code>.csv</code>.</li>
          <li><strong>Zapier / Make:</strong> schedule an HTTP GET to a <code>.json</code> link.</li>
        </ul>
      </Card>
    </>
  );
}

/** Org messaging settings: BCBA nudges, texting, Slack/Teams channel alerts. */
function Messaging({ ctx }: { ctx: Ctx }) {
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () => {
    const [s, p] = await Promise.all([
      sb().from("org_settings").select("*").eq("org_id", ctx.orgId).maybeSingle(),
      sb().from("providers").select("sms_opt_in").eq("org_id", ctx.orgId),
    ]);
    return { s: (must(s) as any) ?? { provider_nudges: true, sms_enabled: true }, optedIn: (must(p) as any[]).filter((x) => x.sms_opt_in).length };
  }, [ctx.orgId]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const cfg = smsConfig();
  return (
    <>
      <Card title="Automatic BCBA reminders">
        <form onSubmit={(e) => {
          const f = formData(e);
          const fe = e.currentTarget as HTMLFormElement;
          const checked = (n: string) => (fe.elements.namedItem(n) as HTMLInputElement).checked;
          run(async () => {
            must(await sb().from("org_settings").upsert({ org_id: ctx.orgId, provider_nudges: checked("provider_nudges"), sms_enabled: checked("sms_enabled"),
              slack_webhook_url: f.slack_webhook_url, teams_webhook_url: f.teams_webhook_url }));
            reload();
          }, "Saved");
        }}>
          <label class="check"><input type="checkbox" name="provider_nudges" checked={data.s.provider_nudges} />
            Nudge BCBAs who haven't finished their portal steps (days 2, 5 and 10 after the invite)</label>
          <label class="check"><input type="checkbox" name="sms_enabled" checked={data.s.sms_enabled} />
            Also text BCBAs who opted in (expiring items and CAQH at 14/7/1 days, plus the nudges). Texts are held 9pm–8am Eastern.</label>
          <p class="small">Email reminders at 30 and 7 days always go out. {data.optedIn} BCBA(s) have opted in to texts.</p>
          <h3>Team channel alerts</h3>
          <p class="small">Posts a morning summary, and an alert when monitoring finds a check that changed to FAIL. Messages contain names and counts only.</p>
          <label>Slack incoming-webhook URL<input name="slack_webhook_url" type="url" placeholder="https://hooks.slack.com/services/…" value={data.s.slack_webhook_url ?? ""} /></label>
          <label>Microsoft Teams webhook URL (Workflows → "Post to a channel when a webhook request is received")
            <input name="teams_webhook_url" type="url" placeholder="https://….webhook.office.com/… or https://….logic.azure.com/…" value={data.s.teams_webhook_url ?? ""} /></label>
          <div class="row-end">
            <button type="button" class="ghost" disabled={busy} onClick={() => run(async () => { await api("/api/app/alerts/test", { org_id: ctx.orgId }); }, "Test sent — check your channel")}>Send test alert</button>
            <button disabled={busy}>Save</button>
          </div>
        </form>
      </Card>
      <Card title="Texting setup">
        <p>Status: {cfg.provider === "none" ? <span class="badge warn">not connected</span> : <span class="badge good">{cfg.provider}</span>}
          {cfg.testMode && <span class="badge warn">test mode: all texts go to the owner's phone</span>}</p>
        <ol class="small">
          <li>Create a Twilio account, buy a local number and create a <em>Messaging Service</em>.</li>
          <li>Register the brand and an A2P 10DLC campaign (use case "Account notifications"); carriers block unregistered business texts. Paste the consent wording BCBAs see in the portal as the opt-in description.</li>
          <li>Set the Messaging Service's incoming-message webhook to <code>{location.origin}/api/sms/inbound</code> (keeps STOP/START in sync).</li>
          <li>Give the Worker <code>SMS_PROVIDER=twilio</code>, <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_MESSAGING_SERVICE_SID</code> and the secret <code>TWILIO_AUTH_TOKEN</code> (see README).</li>
        </ol>
      </Card>
      <OutboxLog ctx={ctx} />
    </>
  );
}
